import type {
	DateValidationError,
	PickerChangeHandlerContext,
} from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { type KeyboardEvent, useState } from "react";

type Draft = { date: Moment | null; isValid: boolean };

/**
 * Holds what the user types in a date filter until the date is finished. Typing goes through
 * intermediate dates (08/30 and 08/01 on the way to 08/15, year 0002 on the way to 2026), and
 * applying each one fired requests and the Start/End rules on dates nobody meant. A calendar pick
 * applies at once; a typed date applies when the field is left or Enter is pressed, and one that is
 * incomplete or out of range goes back to the current value.
 */
export const useDateFilterDraft = (
	value: Moment,
	apply: (date: Moment) => void,
) => {
	const [draft, setDraft] = useState<Draft | null>(null);

	const commit = (date: Moment | null, isValid: boolean) => {
		setDraft(null);
		if (!date || !isValid || !date.isValid() || date.isSame(value, "day")) {
			return;
		}
		apply(date);
	};

	const commitDraft = () => {
		if (draft) commit(draft.date, draft.isValid);
	};

	return {
		pickerProps: {
			value: draft ? draft.date : value,
			onChange: (
				date: Moment | null,
				context: PickerChangeHandlerContext<DateValidationError>,
			) => setDraft({ date, isValid: !context.validationError }),
			onAccept: (
				date: Moment | null,
				context: PickerChangeHandlerContext<DateValidationError>,
			) => commit(date, !context.validationError),
		},
		textFieldProps: {
			onBlur: commitDraft,
			onKeyDown: (event: KeyboardEvent) => {
				if (event.key === "Enter") commitDraft();
			},
		},
	};
};
